import { jsPDF } from "jspdf";
import { DocumentExportMetadata, DocumentSectionRow } from "../../../../shared/document-export";
import { drawPdfConfidentialBanner } from "../../../../shared/document-export/pdf/PdfConfidentialBanner";
import { addPdfDocumentFooters } from "../../../../shared/document-export/pdf/PdfDocumentFooter";
import { drawPdfDocumentHeader } from "../../../../shared/document-export/pdf/PdfDocumentHeader";
import { drawPdfKeyValueList } from "../../../../shared/document-export/pdf/PdfKeyValueList";
import { drawPdfSectionHeading } from "../../../../shared/document-export/pdf/PdfSection";

export interface SessionReportContext {
  serviceName: string;
  instanceStartDate: string;
  instanceStatus?: string;
}

export interface SessionPlanExportData {
  objectives?: string | null;
  activities?: string | null;
  resources?: string | null;
  timingNotes?: string | null;
  responsibilities?: string | null;
  notes?: string | null;
  lastUpdatedDate?: string | null;
}

export interface RetrospectiveExportData {
  outcomes?: string | null;
  whatWorkedWell?: string | null;
  challenges?: string | null;
  lessonsLearned?: string | null;
  followUpActions?: string | null;
  summary?: string | null;
  keyActionPoints?: string | null;
  lastUpdatedDate?: string | null;
}

export interface IncidentExportData {
  serviceInstanceIncidentReportId: number;
  incidentDateTime: string;
  peopleInvolved?: string | null;
  description: string;
  immediateAction?: string | null;
  severity: string;
  safeguardingEscalated: boolean;
  followUpRequired: boolean;
  followUpActions?: string | null;
  status: string;
  createdDate?: string;
  lastUpdatedDate?: string | null;
}

const valueOrFallback = (value?: string | null) => value?.trim() || "Not recorded";

const displayDateTime = (value?: string | null) => {
  if (!value) return "Not recorded";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const sessionSubtitle = ({ instanceStartDate, instanceStatus }: SessionReportContext) =>
  [displayDateTime(instanceStartDate), instanceStatus].filter(Boolean).join(" | ");

const createDocument = (
  title: string,
  documentType: string,
  context: SessionReportContext,
  confidential = false,
) => {
  const metadata: DocumentExportMetadata = {
    documentType,
    title,
    subtitle: sessionSubtitle(context),
    serviceName: context.serviceName,
    createdDate: new Date().toLocaleString(),
    confidential,
  };
  const document = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  document.setProperties({ title, subject: `${title} from FrontLine`, creator: "FrontLine" });
  return { document, metadata };
};

const outputDocument = (document: jsPDF, metadata: DocumentExportMetadata) => {
  addPdfDocumentFooters(document, metadata);
  return document.output("blob");
};

export const generateSessionPlanPdf = async (
  context: SessionReportContext,
  plan: SessionPlanExportData,
): Promise<Blob> => {
  const { document, metadata } = createDocument("Session Plan", "session-plan", context);
  let y = drawPdfDocumentHeader(document, metadata);
  y = drawPdfKeyValueList(document, [
    { label: "Objectives", value: valueOrFallback(plan.objectives) },
    { label: "Activities", value: valueOrFallback(plan.activities) },
    { label: "Resources", value: valueOrFallback(plan.resources) },
    { label: "Timing notes", value: valueOrFallback(plan.timingNotes) },
    { label: "Responsibilities", value: valueOrFallback(plan.responsibilities) },
    { label: "Additional notes", value: valueOrFallback(plan.notes) },
  ], y);
  if (plan.lastUpdatedDate) {
    drawPdfKeyValueList(document, [{ label: "Last saved", value: displayDateTime(plan.lastUpdatedDate) }], y);
  }
  return outputDocument(document, metadata);
};

export const generateRetrospectivePdf = async (
  context: SessionReportContext,
  retrospective: RetrospectiveExportData,
): Promise<Blob> => {
  const { document, metadata } = createDocument("Session Retrospective", "session-retrospective", context);
  let y = drawPdfDocumentHeader(document, metadata);
  y = drawPdfKeyValueList(document, [
    { label: "Outcomes", value: valueOrFallback(retrospective.outcomes) },
    { label: "What worked well", value: valueOrFallback(retrospective.whatWorkedWell) },
    { label: "Challenges", value: valueOrFallback(retrospective.challenges) },
    { label: "Lessons learned", value: valueOrFallback(retrospective.lessonsLearned) },
    { label: "Follow-up actions", value: valueOrFallback(retrospective.followUpActions) },
    { label: "AI-generated summary", value: valueOrFallback(retrospective.summary) },
    { label: "AI-generated key action points", value: valueOrFallback(retrospective.keyActionPoints) },
  ], y);
  if (retrospective.lastUpdatedDate) {
    drawPdfKeyValueList(document, [{ label: "Last saved", value: displayDateTime(retrospective.lastUpdatedDate) }], y);
  }
  return outputDocument(document, metadata);
};

const incidentRows = (incident: IncidentExportData): DocumentSectionRow[] => [
  { label: "Date and time", value: displayDateTime(incident.incidentDateTime) },
  { label: "Severity", value: incident.severity },
  { label: "Status", value: incident.status },
  { label: "People involved", value: valueOrFallback(incident.peopleInvolved) },
  { label: "Description", value: valueOrFallback(incident.description) },
  { label: "Immediate action", value: valueOrFallback(incident.immediateAction) },
  { label: "Safeguarding escalated", value: incident.safeguardingEscalated ? "Yes" : "No" },
  { label: "Follow-up required", value: incident.followUpRequired ? "Yes" : "No" },
  ...(incident.followUpRequired
    ? [{ label: "Follow-up actions", value: valueOrFallback(incident.followUpActions) }]
    : []),
  ...(incident.lastUpdatedDate
    ? [{ label: "Last updated", value: displayDateTime(incident.lastUpdatedDate) }]
    : []),
];

export const generateIncidentsPdf = async (
  context: SessionReportContext,
  incidents: IncidentExportData[],
): Promise<Blob> => {
  if (incidents.length === 0) throw new Error("NO_INCIDENTS");

  const { document, metadata } = createDocument("Incident Report", "session-incidents", context, true);
  const orderedIncidents = [...incidents].sort(
    (left, right) => new Date(left.incidentDateTime).getTime() - new Date(right.incidentDateTime).getTime(),
  );

  orderedIncidents.forEach((item, index) => {
    if (index > 0) document.addPage();
    let y = drawPdfDocumentHeader(document, metadata, index > 0);
    y = drawPdfConfidentialBanner(document, y);
    y = drawPdfSectionHeading(document, `Incident ${index + 1} of ${orderedIncidents.length}`, y);
    drawPdfKeyValueList(document, incidentRows(item), y);
  });

  return outputDocument(document, metadata);
};
